import { useList } from '@refinedev/core'
import { useState } from 'react'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import { AlertTriangle, Search, DollarSign, CreditCard, RefreshCw } from 'lucide-react'

const APIFY_COST_PER_SEARCH = 0.15 // $0.15 per LinkedIn profile search (estimate)

interface CompanyRow extends Record<string, unknown> {
  id: string
  company_name: string
  enable_linkedin_integration: boolean
}

interface SubRow extends Record<string, unknown> {
  id: string
  company_id: string
  active: boolean
  linkedin_searches: number
  linkedin_searches_used?: number
}

export function LinkedinMonitoringPage() {
  const { data: companies } = useList<CompanyRow>({ resource: 'companies', pagination: { pageSize: 200 } })
  const { data: subs } = useList<SubRow>({ resource: 'subscriptions', pagination: { pageSize: 200 } })
  const [refreshing, setRefreshing] = useState(false)

  const companyList = companies?.data ?? []
  const subList = subs?.data ?? []
  const linkedinCompanies = companyList.filter((c) => c.enable_linkedin_integration)

  const rows = linkedinCompanies.map((c) => {
    const sub = subList.find((s) => s.company_id === c.id && s.active)
    const quota = sub?.linkedin_searches ?? 0
    const used = sub?.linkedin_searches_used ?? 0
    const remaining = Math.max(0, quota - used)
    const cost = used * APIFY_COST_PER_SEARCH
    return { ...c, quota, used, remaining, cost, subActive: !!sub }
  })

  const totalSearches = rows.reduce((s, r) => s + r.used, 0)
  const totalCost = rows.reduce((s, r) => s + r.cost, 0)
  const lowQuota = rows.filter((r) => r.subActive && r.remaining < 10)

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-slate-800">LinkedIn Search Monitoring</h1>
          <p className="text-sm text-slate-500 mt-1">Track Apify costs, quota usage, and recharge status.</p>
        </div>
        <button
          className="flex items-center gap-2 px-4 py-2 text-sm border rounded-lg hover:bg-slate-50"
          onClick={() => { setRefreshing(true); setTimeout(() => setRefreshing(false), 1000) }}
        >
          <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-2 text-xs text-slate-500 mb-1"><Search size={13} /> Total Searches</div>
          <div className="text-2xl font-bold">{totalSearches.toLocaleString()}</div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-xs text-slate-500 mb-1"><DollarSign size={13} /> Est. Apify Cost</div>
          <div className="text-2xl font-bold">${totalCost.toFixed(2)}</div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-xs text-slate-500 mb-1"><CreditCard size={13} /> Active Companies</div>
          <div className="text-2xl font-bold">{linkedinCompanies.length}</div>
        </Card>
        <Card className={`p-4 ${lowQuota.length > 0 ? 'border-amber-300 bg-amber-50' : ''}`}>
          <div className="flex items-center gap-2 text-xs text-slate-500 mb-1"><AlertTriangle size={13} /> Low Quota Alerts</div>
          <div className="text-2xl font-bold">{lowQuota.length}</div>
        </Card>
      </div>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Company</TableHead>
              <TableHead>Quota</TableHead>
              <TableHead>Used</TableHead>
              <TableHead>Remaining</TableHead>
              <TableHead>Est. Cost</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell className="font-medium text-xs">{row.company_name}</TableCell>
                <TableCell className="text-xs">{row.quota}</TableCell>
                <TableCell className="text-xs">{row.used}</TableCell>
                <TableCell className="text-xs">
                  <div className="flex items-center gap-2">
                    <Progress value={row.quota > 0 ? (row.remaining / row.quota) * 100 : 0} className="w-20 h-2" />
                    <span className={row.remaining < 10 ? 'text-amber-600 font-semibold' : ''}>{row.remaining}</span>
                  </div>
                </TableCell>
                <TableCell className="text-xs">${row.cost.toFixed(2)}</TableCell>
                <TableCell>
                  {!row.subActive ? (
                    <Badge variant="outline" className="text-[10px] text-slate-400">No Sub</Badge>
                  ) : row.remaining === 0 ? (
                    <Badge variant="outline" className="text-[10px] text-red-600 bg-red-50">Exhausted</Badge>
                  ) : row.remaining < 10 ? (
                    <Badge variant="outline" className="text-[10px] text-amber-600 bg-amber-50">Low</Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px] text-emerald-600 bg-emerald-50">Active</Badge>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-xs text-slate-400 py-8">
                  No companies have LinkedIn integration enabled.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <Card className="p-4 border-amber-200 bg-amber-50/50">
        <div className="flex items-start gap-3">
          <AlertTriangle size={16} className="text-amber-600 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-amber-800">Recharge Configuration</h3>
            <p className="text-xs text-amber-700 mt-1">
              Apify charges approximately ${APIFY_COST_PER_SEARCH.toFixed(2)} per LinkedIn profile search.
              The subscription quota can be recharged from the Service Configuration page.
              Set up alerts at <code className="text-[11px] bg-amber-100 px-1 rounded">my.apify.com</code> for cost thresholds.
            </p>
            <div className="mt-2 flex gap-2">
              <a href="https://console.apify.com" target="_blank" rel="noopener noreferrer"
                className="text-xs text-amber-800 underline hover:text-amber-900">Apify Console</a>
              <span className="text-amber-300">|</span>
              <a href="https://my.apify.com/billing" target="_blank" rel="noopener noreferrer"
                className="text-xs text-amber-800 underline hover:text-amber-900">Billing Dashboard</a>
            </div>
          </div>
        </div>
      </Card>
    </div>
  )
}
